import store from '@/state/store'

/**
 * one_inbox routes.
 *
 * Three screens are the product — the desk, the knowledge base, and bot health.
 * The rest is the account and admin spine, ported back from one_sales in
 * 2026-10 (the clone had pruned it, which is why adding a login previously
 * needed an SSH session).
 *
 * ── `meta.permission` is a courtesy, not the control ────────────────────
 * router/index.js redirects to `/` when the cached permission list does not
 * carry the named right, and side-bar.vue hides the entry. Both read the SAME
 * cached list, which the server refreshes once per app load — so a right
 * revoked mid-session is still in localStorage until then. The API refuses the
 * request regardless; these strings exist so somebody does not land on a screen
 * whose every call 403s. The names must match PermissionAndRoleSeeder exactly.
 */
export default [
    // ── Public ──
    { path: '/login', name: 'login', component: () => import('../views/pages/account/login'),
        meta: { beforeResolve(routeTo, routeFrom, next) { if (store.getters['auth/loggedIn']) { next({ name: 'home' }) } else { next() } } } },
    { path: '/forgot-password', name: 'Forgot-password', component: () => import('../views/pages/account/forgot-password'),
        meta: { beforeResolve(routeTo, routeFrom, next) { if (store.getters['auth/loggedIn']) { next({ name: 'home' }) } else { next() } } } },
    { path: '/reset-password', name: 'Reset password', component: () => import('../views/pages/account/create-password-forgot'),
        meta: { beforeResolve(routeTo, routeFrom, next) { next() } } },
    { path: '/first-time-password', name: 'Create-password', component: () => import('../views/pages/account/create-password'),
        meta: { beforeResolve(routeTo, routeFrom, next) { next() } } },
    { path: '/logout', name: 'logout',
        meta: { authRequired: true, async beforeResolve(routeTo, routeFrom, next) {
            await store.dispatch('auth/logout')
            const authRequiredOnPreviousRoute = routeFrom.matched.some((route) => route.push('/login'))
            next(authRequiredOnPreviousRoute ? { name: 'home' } : { ...routeFrom })
        } } },

    // ── The desk ──
    //
    // The inbox is home. An agent opening this app is opening it to answer
    // somebody, not to look at a dashboard.
    { path: '/', name: 'home', redirect: '/inbox' },
    { path: '/inbox', name: 'Inbox', meta: { authRequired: true },
        component: () => import('../views/pages/inbox/inbox') },

    /*
     * Appointments the bot took, for the desk that has to keep them.
     *
     * No `permission:` — same reasoning as the bot screens below, and the API
     * group carries none either. `feature` is the real gate: it follows the
     * appointment_host_at setting, so when the schedule is kept in another
     * system this route redirects home instead of showing an empty one.
     */
    { path: '/appointments', name: 'Appointments', meta: { authRequired: true, feature: 'appointments' },
        component: () => import('../views/pages/appointment/appointmentList') },

    // ── The bot ──
    //
    // No `permission:` on these two, deliberately. spatie permissions gate the
    // admin screens; who may edit a brand's knowledge base is already answered
    // by which tenant the agent belongs to (App\Support\TenantContext), and a
    // second gate here would be a second place to get it wrong.
    { path: '/knowledge-base', name: 'Knowledge-base', meta: { authRequired: true },
        component: () => import('../views/pages/faq/faqEditor') },
    // Same gate as the knowledge base, for the same reason: a brand's game
    // tips are content its own people edit, and which brand is already
    // answered by TenantContext on the server.
    { path: '/game-tips', name: 'Game-tips', meta: { authRequired: true, feature: 'game_tips' },
        component: () => import('../views/pages/gameTips/gameTips') },

    // ── The team ──
    //
    // Visible to everyone, writable only by users.is_admin. The screen hides its
    // controls on `can_manage` from the API and the server enforces it again —
    // a hidden button is not a permission.
    //
    // Kept alongside /users on purpose: this one needs no mail server and no
    // spatie role, which is what a CS supervisor on shift actually has. See
    // the backend's UserController for the full distinction.
    { path: '/team', name: 'Team', meta: { authRequired: true },
        component: () => import('../views/pages/team/team') },

    // ── Admin ──
    //
    // Listing / form pairs, all following the same shape: the listing owns the
    // search criteria (stashed per screen in session storage) and the form is
    // create-and-edit in one component, keyed off `params.id`.

    { path: '/users', name: 'Users-listing',
        meta: { authRequired: true, permission: 'Read User' },
        component: () => import('../views/pages/user/userListing') },
    { path: '/users/create', name: 'User-create',
        meta: { authRequired: true, permission: 'Create User' },
        component: () => import('../views/pages/user/userForm') },
    // `/users/create` is declared BEFORE `/users/:id`, or the wildcard
    // swallows it and the create screen loads as "user not found" — the same
    // ordering trap routes/api.php carries a note about.
    { path: '/users/:id', name: 'User-update',
        meta: { authRequired: true, permission: 'Update User' },
        component: () => import('../views/pages/user/userForm') },

    { path: '/roles', name: 'Roles-listing',
        meta: { authRequired: true, permission: 'Read Role' },
        component: () => import('../views/pages/role/roleListing') },
    { path: '/roles/create', name: 'Role-create',
        meta: { authRequired: true, permission: 'Create Role' },
        component: () => import('../views/pages/role/roleForm') },
    { path: '/roles/:id', name: 'Role-update',
        meta: { authRequired: true, permission: 'Update Role' },
        component: () => import('../views/pages/role/roleForm') },

    // Keyed by `key`, not by an id: a configuration row IS its key, and the
    // API is /v1/configurations/{key}.
    { path: '/configurations', name: 'Configurations-listing',
        meta: { authRequired: true, permission: 'Read Configuration' },
        component: () => import('../views/pages/configuration/configurationListing') },
    { path: '/configurations/:key', name: 'Configuration-update',
        meta: { authRequired: true, permission: 'Update Configuration' },
        component: () => import('../views/pages/configuration/configurationUpdate') },

    // Organisations = our CLIENTS' brands. Platform staff only, enforced
    // server-side as well — the brand list is the customer list.
    { path: '/organizations', name: 'Organizations-listing',
        meta: { authRequired: true, permission: 'Read Organisation' },
        component: () => import('../views/pages/organization/organizationListing') },
    { path: '/organizations/create', name: 'Organization-create',
        meta: { authRequired: true, permission: 'Create Organisation' },
        component: () => import('../views/pages/organization/organizationForm') },
    { path: '/organizations/:tenantId', name: 'Organization-detail', meta: { authRequired: true },
        component: () => import('../views/pages/organization/organizationDetail') },

    /*
     * Industry packs = what one trade's customers sound like: the glossary,
     * the labelled examples the tone reading learns from, and the lines the
     * bot says in that trade. One pack serves every brand whose Industry
     * names it, so it is edited here and never from a brand's page.
     */
    /*
     * The change log — who edited a brand's FAQ and what it said before.
     * NOT the knowledge base's own "audit", which grades the FAQ's quality;
     * that one lives inside the editor at /knowledge-base.
     */
    { path: '/audit-log', name: 'Audit-log-listing',
        meta: { authRequired: true, permission: 'Read Audit Log' },
        component: () => import('../views/pages/auditLog/auditLogList') },
    { path: '/industry-packs', name: 'Industry-packs',
        meta: { authRequired: true, permission: 'Read Organisation' },
        component: () => import('../views/pages/tonePack/tonePackList') },
    { path: '/industry-packs/:name', name: 'Industry-pack-detail',
        meta: { authRequired: true, permission: 'Read Organisation' },
        component: () => import('../views/pages/tonePack/tonePackDetail') },

    // Proposals = briefs the bot collected for a brand and the documents drafted
    // from them. Platform staff only, same permission as the brand list.
    { path: '/proposals', name: 'Proposals-listing',
        meta: { authRequired: true, permission: 'Read Organisation', feature: 'proposal' },
        component: () => import('../views/pages/proposal/proposalListing') },
    { path: '/proposals/:leadRef', name: 'Proposal-detail',
        meta: { authRequired: true, permission: 'Read Organisation', feature: 'proposal' },
        component: () => import('../views/pages/proposal/proposalDetail') },

]
